/**
 * Deep-merges the ECONIC locale areas (`<area>.en.ts` / `<area>.ar.ts`, one file pair per work package, SPEC C9) into one tree.
 *
 * Two areas may share a branch (`econic.*`), never a leaf: a key path that ends in a value in one area and exists in another is a
 * duplicate. In development (and under `node --test`) that throws, naming the path and both areas, so a clash cannot ship; in a production
 * build it is reported on the console and the first area's value is kept. The inputs are never mutated.
 */

type Tree = { [key: string]: unknown };

type UnionToIntersection<U> = (U extends unknown ? (x: U) => void : never) extends (x: infer I) => void ? I : never;

/** The merged type of a list of locale areas: every area's keys, intersected branch by branch. */
export type Merged<T extends readonly object[]> = UnionToIntersection<T[number]>;

export interface LocaleDuplicate {
  /** Dotted key path, e.g. `econic.shell.route.today`. */
  path: string;
  /** The area that defined it first and the area that defined it again. */
  areas: [string, string];
}

const isBranch = (value: unknown): value is Tree => typeof value === "object" && value !== null && !Array.isArray(value);

function isDevelopment(): boolean {
  // Vite sets `import.meta.env.DEV`; anything else (Node tests, scripts) counts as development, so duplicates fail loudly there.
  return (import.meta as { env?: { DEV?: boolean } }).env?.DEV !== false;
}

function mergeInto(target: Tree, source: Tree, owners: Map<string, string>, area: string, prefix: string, found: LocaleDuplicate[]): void {
  for (const [key, value] of Object.entries(source)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (!Object.prototype.hasOwnProperty.call(target, key)) {
      owners.set(path, area);
      if (isBranch(value)) {
        const branch: Tree = {};
        target[key] = branch;
        mergeInto(branch, value, owners, area, path, found);
      } else {
        target[key] = value;
      }
      continue;
    }
    const existing = target[key];
    if (isBranch(existing) && isBranch(value)) {
      mergeInto(existing, value, owners, area, path, found);
      continue;
    }
    // A leaf meets a leaf, or a leaf meets a branch: the same key path is claimed twice. The first value stays.
    found.push({ path, areas: [owners.get(path) ?? "?", area] });
  }
}

/** Merges areas given as `[name, tree]` pairs; returns the tree and every duplicate found (it does not throw). */
export function mergeLocaleAreas(areas: ReadonlyArray<readonly [string, object]>): { tree: Tree; duplicates: LocaleDuplicate[] } {
  const tree: Tree = {};
  const owners = new Map<string, string>();
  const duplicates: LocaleDuplicate[] = [];
  for (const [name, area] of areas) {
    if (!isBranch(area)) throw new TypeError(`ECONIC locale area "${name}" is not an object`);
    mergeInto(tree, area, owners, name, "", duplicates);
  }
  return { tree, duplicates };
}

/** The duplicate leaf paths between areas (empty when they merge cleanly). */
export function findDuplicateKeys(areas: ReadonlyArray<readonly [string, object]>): LocaleDuplicate[] {
  return mergeLocaleAreas(areas).duplicates;
}

/**
 * Merges named locale areas into one tree. Throws on a duplicate leaf key in development; in production logs it and keeps the first value.
 * Usage: `deepMerge(["shell", shellEn], ["kb", kbEn], …)`.
 */
export function deepMerge<T extends readonly object[]>(...areas: { [K in keyof T]: readonly [string, T[K]] }): Merged<T> {
  const { tree, duplicates } = mergeLocaleAreas(areas as unknown as ReadonlyArray<readonly [string, object]>);
  if (duplicates.length) {
    const message = `Duplicate ECONIC locale keys: ${duplicates.map((d) => `${d.path} (${d.areas[0]} and ${d.areas[1]})`).join("; ")}`;
    if (isDevelopment()) throw new Error(message);
    console.error(message);
  }
  return tree as Merged<T>;
}
